import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useBlocker } from "react-router";
import { ApiError } from "./api";
import { Icon } from "./ui";

/**
 * Editing, to the familiar admin's pattern: a page's form is a draft of a saved
 * document; the moment it differs, the top bar's search gives way to the
 * contextual save bar (Unsaved changes · Discard · Save); Save sends the
 * whole document with the revision it was loaded at; a field error lands
 * under its field, a conflict asks for a reload, success is a toast; leaving
 * with unsaved changes asks first.
 */

/* ---------- the save bar, shared through the shell ---------- */

export interface SaveBarState {
  saving: boolean;
  save: () => void;
  discard: () => void;
}

// Two contexts on purpose: editors only ever read the stable setter, so a change
// to the bar re-renders the top bar and nothing else (an editor that re-rendered
// on its own bar would hand the shell a new bar again, without end).
const SaveBarValue = createContext<SaveBarState | null>(null);
const SaveBarSetter = createContext<(b: SaveBarState | null) => void>(() => undefined);

export function SaveBarProvider({ children }: { children: ReactNode }) {
  const [bar, setBar] = useState<SaveBarState | null>(null);
  return (
    <SaveBarSetter.Provider value={setBar}>
      <SaveBarValue.Provider value={bar}>{children}</SaveBarValue.Provider>
    </SaveBarSetter.Provider>
  );
}

export function useSaveBar() {
  return useContext(SaveBarValue);
}

export function SaveBar({ bar }: { bar: SaveBarState }) {
  return (
    <div className="savebar" role="status" aria-live="polite">
      <span className="savebar__note">
        <Icon.info />
        <span>Unsaved changes</span>
      </span>
      <div className="savebar__actions">
        <button type="button" className="savebar__discard" onClick={bar.discard} disabled={bar.saving}>
          Discard
        </button>
        <button type="button" className="savebar__save" onClick={bar.save} disabled={bar.saving}>
          {bar.saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

/* ---------- toasts ---------- */

const TOAST_EVENT = "mivio:toast";

export function toast(text: string, tone: "plain" | "error" = "plain"): void {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { text, tone } }));
}

export function ToastHost() {
  const [items, setItems] = useState<{ id: number; text: string; tone: string }[]>([]);
  useEffect(() => {
    let n = 0;
    const on = (e: Event) => {
      const { text, tone } = (e as CustomEvent<{ text: string; tone: string }>).detail;
      const id = ++n;
      setItems((list) => [...list.slice(-2), { id, text, tone }]);
      window.setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), 4000);
    };
    window.addEventListener(TOAST_EVENT, on);
    return () => window.removeEventListener(TOAST_EVENT, on);
  }, []);
  if (!items.length) return null;
  return (
    <div className="toasts" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast${t.tone === "error" ? " toast--error" : ""}`}>
          {t.tone === "error" ? <Icon.alert /> : <Icon.check />}
          {t.text}
        </div>
      ))}
    </div>
  );
}

/* ---------- banners ---------- */

export type BannerTone = "critical" | "warning" | "success" | "info";

export function Banner({ tone = "info", title, children, action }: { tone?: BannerTone; title?: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={`banner banner--${tone}`} role={tone === "critical" ? "alert" : "status"}>
      <span className="banner__icon">{tone === "critical" || tone === "warning" ? <Icon.alert /> : tone === "success" ? <Icon.check /> : <Icon.info />}</span>
      <div className="banner__body">
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
      {action && <div className="banner__action">{action}</div>}
    </div>
  );
}

/* ---------- the editor ---------- */

export type FieldErrors = Record<string, string>;

export interface EditorNotice {
  tone: BannerTone;
  title: string;
  text?: string;
  conflict?: boolean;
}

export interface EditorOptions<T> {
  /** The saved document, as loaded. When it changes while the draft is clean, the draft follows it. */
  initial: T;
  /** The revision the document was loaded at; sent back as expected_revision. */
  revision: number;
  /** Sends the whole draft; answers with what was saved and its new revision. */
  save: (draft: T, expectedRevision: number) => Promise<{ document: T; revision: number; savedText?: string }>;
  /** The toast on success. */
  savedText?: string;
}

function flatErrors(input: unknown, prefix = ""): FieldErrors {
  const out: FieldErrors = {};
  if (!input || typeof input !== "object") return out;
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out[path] = v;
    else if (v && typeof v === "object") Object.assign(out, flatErrors(v, path));
  }
  return out;
}

export function useEditor<T extends object>(opts: EditorOptions<T>) {
  // The options object is new on every render of the page; the callbacks read the latest through a ref
  // so their own identities only change when the draft or the revision does.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const initialJson = JSON.stringify(opts.initial);
  const [saved, setSaved] = useState<{ doc: T; json: string; revision: number }>({ doc: opts.initial, json: initialJson, revision: opts.revision });
  const [draft, setDraft] = useState<T>(opts.initial);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [notice, setNotice] = useState<EditorNotice | null>(null);
  const draftJson = JSON.stringify(draft);
  const dirty = draftJson !== saved.json;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // A fresh load of the same record (a refetch, a reload after a conflict) replaces a clean draft.
  useEffect(() => {
    if (initialJson === saved.json && opts.revision === saved.revision) return;
    if (dirtyRef.current) return;
    setSaved({ doc: opts.initial, json: initialJson, revision: opts.revision });
    setDraft(opts.initial);
    setErrors({});
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJson, opts.revision]);

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => {
      if (!(key as string in e)) return e;
      const next = { ...e };
      delete next[key as string];
      return next;
    });
  }, []);

  const discard = useCallback(() => {
    setDraft(saved.doc);
    setErrors({});
    setNotice(null);
  }, [saved.doc]);

  /**
   * The record changed on the server underneath this draft (an image picked while editing): the new
   * version becomes the saved one, and the fields this person has changed and not yet saved are kept.
   */
  const rebase = useCallback(
    (doc: T, revision: number) => {
      const before = saved.doc as Record<string, unknown>;
      const after = doc as Record<string, unknown>;
      const changed = (k: string) => JSON.stringify(after[k]) !== JSON.stringify(before[k]);
      setSaved({ doc, json: JSON.stringify(doc), revision });
      setDraft((d) => {
        const next: Record<string, unknown> = { ...after };
        for (const [k, v] of Object.entries(d as Record<string, unknown>)) if (JSON.stringify(v) !== JSON.stringify(before[k])) next[k] = v;
        return next as T;
      });
      setErrors((e) => {
        const next = { ...e };
        for (const k of Object.keys(next)) if (changed(k.split(".")[0]!)) delete next[k];
        return next;
      });
      setNotice(null);
    },
    [saved.doc],
  );

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setErrors({});
    setNotice(null);
    try {
      const result = await optsRef.current.save(draft, saved.revision);
      const json = JSON.stringify(result.document);
      setSaved({ doc: result.document, json, revision: result.revision });
      setDraft(result.document);
      toast(result.savedText ?? optsRef.current.savedText ?? "Saved");
    } catch (err) {
      if (err instanceof ApiError) {
        const fields = flatErrors(err.errors);
        if (err.status === 422 || Object.keys(fields).length) {
          setErrors(fields);
          const general = fields._request;
          setNotice({ tone: "critical", title: "Nothing was saved", text: general ?? (Object.keys(fields).length ? "Fix the highlighted fields and save again." : err.message) });
        } else if (err.status === 409) {
          setNotice({ tone: "warning", title: "This record changed while you were editing", text: err.message, conflict: true });
        } else if (err.status === 403) {
          setNotice({ tone: "critical", title: "Your account cannot make this change", text: err.message });
        } else {
          setNotice({ tone: "critical", title: "The save did not go through", text: err.message });
        }
      } else {
        setNotice({ tone: "critical", title: "The save did not go through", text: "Check your connection and try again. Nothing was saved." });
      }
    } finally {
      setSaving(false);
    }
  }, [draft, saved.revision, saving]);

  // Hand the shell the save bar while the draft is dirty.
  const setBar = useContext(SaveBarSetter);
  useEffect(() => {
    setBar(dirty ? { saving, save: () => void save(), discard } : null);
  }, [dirty, saving, save, discard, setBar]);
  useEffect(() => () => setBar(null), [setBar]);

  // Leaving with unsaved changes asks first: in the app through the router, and on close or reload through the browser.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ⌘S / Ctrl+S saves.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirtyRef.current) void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const leaveGuard =
    blocker.state === "blocked" ? (
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="leave-title">
        <button type="button" className="modal__scrim" aria-label="Stay on this page" onClick={() => blocker.reset()} />
        <div className="modal__card">
          <h2 id="leave-title">Leave page with unsaved changes?</h2>
          <p>Leaving this page will discard the changes you have not saved.</p>
          <div className="modal__actions">
            <button type="button" className="btn" onClick={() => blocker.reset()}>
              Stay
            </button>
            <button type="button" className="btn btn--danger" onClick={() => blocker.proceed()}>
              Leave page
            </button>
          </div>
        </div>
      </div>
    ) : null;

  return {
    draft,
    set,
    setDraft,
    rebase,
    dirty,
    saving,
    errors,
    notice,
    clearNotice: () => setNotice(null),
    save,
    discard,
    revision: saved.revision,
    leaveGuard,
  };
}
